import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BASE}/api`,
  headers: { "Content-Type": "application/json" },
});

/* ------------------------------------------------------------------ *
 * Website lead capture — ERPNext v16 Lead
 *
 * Every form on the site (demo, contact, brochure download, newsletter)
 * uses the SAME field names and posts through the single `submitLead`
 * function below. Field names are ERPNext Lead fieldnames, so the
 * payload maps 1:1 onto the Lead doctype — no translation layer.
 *
 * To change where leads go, change LEAD_ENDPOINT. Nothing else.
 * ------------------------------------------------------------------ */

const LEAD_ENDPOINT =
  process.env.REACT_APP_LEAD_ENDPOINT ||
  "https://erp.skillsgain.com/api/method/common_customization.api.website_lead.capture_website_lead";

// Lead.source is a Link to the Lead Source doctype, so it must be an
// existing record. Which form/page the lead came from goes into notes.
const LEAD_SOURCE = "Website";

/**
 * The shared shape for every lead form on the site.
 * Keys are ERPNext Lead fieldnames — use these exact names for the
 * `name` attribute and state key of every form input.
 */
export const emptyLeadForm = () => ({
  name: "",          // Full Name
  email: "",         // Work / Business Email
  mobile_no: "",     // Phone  (also sent as `phone` — see buildLeadPayload)
  company_name: "",  // Company / Organization Name
  job_title: "",     // Job Title
  interest: "",      // Area of Interest  (folded into notes — see below)
  notes: "",         // Message
});

const clean = (v) => (typeof v === "string" ? v.trim() : v) || undefined;

/**
 * `interest` and the form origin have no standard Lead fieldname, so they
 * are appended to notes. If ERPNext has custom fields for them, send them
 * as extra keys here instead (e.g. custom_area_of_interest).
 */
const buildLeadPayload = ({ interest, notes, source, formType, ...fields }) => {
  const trailer = [
    interest ? `Area of Interest: ${clean(interest)}` : undefined,
    formType ? `Form: ${clean(formType)}` : undefined,
    source ? `Page: ${clean(source)}` : undefined,
  ].filter(Boolean);

  const payload = { source: LEAD_SOURCE };

  Object.entries(fields).forEach(([key, value]) => {
    const v = clean(value);
    if (v !== undefined) payload[key] = v;
  });

  // Lead has both a Phone and a Mobile No field — fill both with the same number.
  if (payload.mobile_no && !payload.phone) {
    payload.phone = payload.mobile_no;
  }

  // Newsletter has no name field — fall back to the email local part.
  if (!payload.name && payload.email) {
    payload.name = payload.email.split("@")[0];
  }

  const body = [clean(notes), ...trailer].filter(Boolean).join("\n");
  if (body) payload.notes = body;

  return payload;
};

// Frappe reports failures via `exception` / `_server_messages`, not `detail`.
// Normalise to `err.response.data.detail` so every form can read one field.
const normaliseLeadError = (err) => {
  const data = err?.response?.data;
  let detail;

  try {
    const serverMessages = data?._server_messages ? JSON.parse(data._server_messages) : [];
    const first = serverMessages[0];
    const parsed = typeof first === "string" ? JSON.parse(first) : first;
    detail = parsed?.message || parsed;
  } catch (e) {
    // Server messages weren't JSON — fall through to the other fields.
  }

  detail = detail || data?.message || data?.exception || data?.detail;

  if (typeof detail === "string" && err.response) {
    err.response.data = { ...data, detail: detail.replace(/<[^>]*>/g, "").trim() };
  }

  return err;
};

/**
 * The one and only form submission function.
 * @param {object} form - an emptyLeadForm() shape, plus { source, formType }.
 */
export const submitLead = (form) =>
  axios
    .post(LEAD_ENDPOINT, buildLeadPayload(form || {}), {
      headers: { "Content-Type": "application/json" },
    })
    .then((r) => r.data?.message ?? r.data)
    .catch((err) => {
      throw normaliseLeadError(err);
    });

// Admin dashboard still reads from the local backend.
export const fetchLeads = (params = {}) => api.get("/leads", { params }).then((r) => r.data);
export const fetchLeadStats = () => api.get("/leads/stats").then((r) => r.data);
